import React, { Suspense } from 'react';

import { lazyWithReload } from 'helpers/lazyWithReload';
import Overlay from 'components/v2/overlay/Overlay';
import BookingPhotographerModal from './tasks/BookingPhotographerModal';
import { MODAL_ID, MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import GlobalSearch from 'components/v2/shared/GlobalSearch';
import VirtualStagingModal from './tasks/VirtualStagingModal';
import MaterialDownloadModal from './cases/MaterialDownloadModal';
import AITextInputTaskModal from './tasks/AITextInputTaskModal';
import AITextOverviewTaskModal from './tasks/AITextOverviewTaskModal';

const UploadFilesModal = lazyWithReload(() => import('./tasks/UploadFilesModal'));
const InputModal = lazyWithReload(() => import('./tasks/InputModal'));
const TextTaskModal = lazyWithReload(() => import('./tasks/TextTaskModal'));
const SocialMediaSlideShowModal = lazyWithReload(() => import('./tasks/SocialMediaSlideShowModal'));
const PersonInformationModal = lazyWithReload(() => import('./tasks/PersonInformationModal'));
const VidibiliCommentModal = lazyWithReload(() => import('./tasks/VidibiliCommentModal'));
const CopyWriteTextsModal = lazyWithReload(() => import('./tasks/CopyWriteTextsModal'));
const PersonModal = lazyWithReload(() => import('./tasks/PersonModal'));
const IntroOutroSpeechModal = lazyWithReload(() => import('./tasks/IntroOutroSpeechModal'));
const SpeakInputModal = lazyWithReload(() => import('./tasks/SpeakInputModal'));
const SocialMediaModal = lazyWithReload(() => import('./tasks/SocialMediaModal'));
const ComtextModal = lazyWithReload(() => import('./tasks/ComtextModal'));
const AerialPhotoModal = lazyWithReload(() => import('./tasks/AerialPhotoModal'));
const RetouchingModal = lazyWithReload(() => import('./tasks/RetouchingModal'));
const CombineImagesModal = lazyWithReload(() => import('./tasks/CombineImagesModal'));
const BeforeAndAfterModal = lazyWithReload(() => import('./tasks/BeforeAndAfterModal'));
const ImageSelectorModal = lazyWithReload(() => import('./tasks/ImageSelectorModal'));
const LocationModal = lazyWithReload(() => import('./tasks/LocationModal'));
const IntroOutroModal = lazyWithReload(() => import('./tasks/IntroOutroModal'));
const FeedbackPageContentModal = lazyWithReload(() => import('./order/FeedbackPageContentModal'));
const TextTaskNotesModal = lazyWithReload(() => import('./order/TextTaskNotesModal'));
const SocialMediaAccountModal = lazyWithReload(() => import('./branch/SocialMediaAccountModal'));
const DeleteSocialMediaAccountModal = lazyWithReload(() => import('./branch/DeleteSocialMediaAccountModal'));

const TASKS_MODALS = [
  {
    id: TASKS_MODAL_ID.TEXT,
    element: <TextTaskModal />,
  },
  {
    id: TASKS_MODAL_ID.SOCIAL_MEDIA_SLIDESHOW,
    element: <SocialMediaSlideShowModal />,
  },
  {
    id: TASKS_MODAL_ID.UPLOAD,
    element: <UploadFilesModal />,
  },
  {
    id: TASKS_MODAL_ID.INPUT_TEXT,
    element: <InputModal />,
  },
  {
    id: TASKS_MODAL_ID.PERSON_INFO,
    element: <PersonInformationModal />,
  },
  {
    id: TASKS_MODAL_ID.VIDIBILI_COMMENT,
    element: <VidibiliCommentModal />,
  },
  {
    id: TASKS_MODAL_ID.COPYWRITE_TEXTS,
    element: <CopyWriteTextsModal />,
  },
  {
    id: TASKS_MODAL_ID.PERSON,
    element: <PersonModal />,
  },
  {
    id: TASKS_MODAL_ID.INTRO_OUTRO_SPEAK,
    element: <IntroOutroSpeechModal />,
  },
  {
    id: TASKS_MODAL_ID.SPEAK_INPUT,
    element: <SpeakInputModal />,
  },
  {
    id: TASKS_MODAL_ID.SOCIAL_MEDIA,
    element: <SocialMediaModal />,
  },
  {
    id: TASKS_MODAL_ID.COMTEXT,
    element: <ComtextModal />,
  },
  {
    id: TASKS_MODAL_ID.ARIAL,
    element: <AerialPhotoModal />,
  },
  {
    id: TASKS_MODAL_ID.BOOK_PHOTOGRAPHER,
    element: <BookingPhotographerModal />,
  },
  {
    id: TASKS_MODAL_ID.IMAGE_SELECTORS,
    element: <ImageSelectorModal />,
  },
  {
    id: TASKS_MODAL_ID.RETOUCHING_IMAGE,
    element: <RetouchingModal />,
  },
  {
    id: TASKS_MODAL_ID.BEFORE_AFTER,
    element: <BeforeAndAfterModal />,
  },
  {
    id: TASKS_MODAL_ID.COMBINE_IMAGES,
    element: <CombineImagesModal />,
  },
  {
    id: TASKS_MODAL_ID.LOCATION,
    element: <LocationModal />,
  },
  {
    id: TASKS_MODAL_ID.INTRO_OUTRO,
    element: <IntroOutroModal />,
  },
  {
    id: TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE,
    element: <VirtualStagingModal />,
  },
  {
    id: TASKS_MODAL_ID.AITextInput,
    element: <AITextInputTaskModal />,
  },
  {
    id: TASKS_MODAL_ID.AITextOverview,
    element: <AITextOverviewTaskModal />,
  },
];

const ORDERS_MODAL = [
  {
    id: MODAL_ID.ADD_FEEDBACK_ON_ORDER_LINE,
    element: <FeedbackPageContentModal />,
  },
  {
    id: MODAL_ID.TEXT_TASK_NOTES,
    element: <TextTaskNotesModal />,
  },
];

const CASE_MODAL = [
  {
    id: MODAL_ID.CASES_MATERIALS_DOWNLOAD,
    element: <MaterialDownloadModal />,
  },
];

const BRANCH_MODAL = [
  {
    id: MODAL_ID.ADD_EDIT_SOCIAL_MEDIA_ACCOUNT,
    element: <SocialMediaAccountModal />,
  },
  {
    id: MODAL_ID.DELETE_SOCIAL_MEDIA_ACCOUNT,
    element: <DeleteSocialMediaAccountModal />,
  },
];

const ServicesModal = () => {
  const { isModalOpen: isOpenTask } = MODALS_STORE.tasks();
  const { isModalOpen: isOpenGeneral } = MODALS_STORE.general();
  const { isModalOpen: isOpenOrder } = MODALS_STORE.orders();
  const { isModalOpen: isOpenCase } = MODALS_STORE.cases();
  const { isModalOpen: isOpenBranch } = MODALS_STORE.branch();

  return (
    <Suspense fallback={<Overlay />}>
      {TASKS_MODALS.filter(m => isOpenTask(m.id)).map(m => (
        <React.Fragment key={m.id}>{m.element}</React.Fragment>
      ))}

      {ORDERS_MODAL.filter(m => isOpenOrder(m.id)).map(m => (
        <React.Fragment key={m.id}>{m.element}</React.Fragment>
      ))}

      {CASE_MODAL.filter(m => isOpenCase(m.id)).map(m => (
        <React.Fragment key={m.id}>{m.element}</React.Fragment>
      ))}

      {BRANCH_MODAL.filter(m => isOpenBranch(m.id)).map(m => (
        <React.Fragment key={m.id}>{m.element}</React.Fragment>
      ))}

      {/* Do not suspend */}
      {isOpenGeneral(MODAL_ID.GLOBAL_SEARCH) && <GlobalSearch />}
    </Suspense>
  );
};

export default ServicesModal;
